import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, ShieldCheck, FileSignature, Video, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const JITSI_SCRIPT_ID = 'jitsi-external-api-script';

function loadJitsiScript(domain) {
  return new Promise((resolve, reject) => {
    if (window.JitsiMeetExternalAPI) return resolve(window.JitsiMeetExternalAPI);
    const existing = document.getElementById(JITSI_SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.JitsiMeetExternalAPI));
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.id = JITSI_SCRIPT_ID;
    s.src = `https://${domain}/external_api.js`;
    s.async = true;
    s.onload = () => resolve(window.JitsiMeetExternalAPI);
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

/**
 * Jitsi-backed telehealth room.
 *
 * Flow:
 *   1. Fetch jitsi-config (domain + room + display name).
 *   2. If a patient_id is associated with the session, require a signed
 *      BAA consent before embedding the Jitsi iframe. Existing consents
 *      for the same appointment are honoured.
 *   3. Embed Jitsi via the official external_api.js loader.
 *
 * No third-party SDK install required — Jitsi loads at runtime from CDN.
 */
export default function TelehealthRoom() {
  const { roomName } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [baa, setBaa] = useState({ checking: true, hasConsent: false, terms: '', termsVersion: '1.0' });
  const [showBaa, setShowBaa] = useState(false);
  const apiRef = useRef(null);
  const containerRef = useRef(null);

  // ---------- Load config + BAA state ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/telehealth/jitsi-config/${roomName}`, { headers: authHeader() });
        if (cancelled) return;
        setConfig(r.data);
        // If there's a patient on this session, check for an existing BAA consent
        if (r.data.patient_id) {
          const q = new URLSearchParams({ patient_id: r.data.patient_id });
          if (r.data.appointment_id) q.set('appointment_id', r.data.appointment_id);
          const cr = await axios.get(`${API_URL}/api/telehealth/baa-consent?${q}`, { headers: authHeader() });
          if (cancelled) return;
          setBaa({ checking: false,
                   hasConsent: !!cr.data.has_consent,
                   terms: cr.data.terms_text || cr.data.consent?.terms_text_snapshot || '',
                   termsVersion: cr.data.terms_version || '1.0' });
          if (!cr.data.has_consent) setShowBaa(true);
        } else {
          // Provider-only or no patient — skip BAA gate
          setBaa(b => ({ ...b, checking: false, hasConsent: true }));
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Failed to load room');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [roomName]);

  // ---------- Embed Jitsi once we have config + (consent OR no patient) ----------
  const startJitsi = useCallback(async () => {
    if (!config || !containerRef.current || apiRef.current) return;
    try {
      const JitsiAPI = await loadJitsiScript(config.domain);
      apiRef.current = new JitsiAPI(config.domain, {
        roomName: config.room_name,
        parentNode: containerRef.current,
        userInfo: {
          displayName: config.display_name,
          email: config.email,
        },
        configOverwrite: {
          startWithAudioMuted: false,
          startWithVideoMuted: false,
          prejoinPageEnabled: false,
          disableDeepLinking: true,
        },
        interfaceConfigOverwrite: {
          SHOW_JITSI_WATERMARK: false,
          SHOW_PROMOTIONAL_CLOSE_PAGE: false,
        },
      });
      apiRef.current.addListener('readyToClose', () => {
        navigate('/scheduling');
      });
    } catch (e) {
      setError('Failed to start Jitsi: ' + (e.message || 'unknown error'));
    }
  }, [config, navigate]);

  useEffect(() => {
    if (loading || baa.checking) return;
    if (!baa.hasConsent) return;
    startJitsi();
    return () => {
      try { apiRef.current?.dispose(); } catch (_e) {}
      apiRef.current = null;
    };
  }, [loading, baa.checking, baa.hasConsent, startJitsi]);

  const handleSigned = async (signed_name, relationship) => {
    if (!config?.patient_id) return;
    try {
      await axios.post(`${API_URL}/api/telehealth/baa-consent`, {
        patient_id: config.patient_id,
        appointment_id: config.appointment_id,
        room_name: config.room_name,
        signed_name,
        relationship: relationship || 'self',
        agreed: true,
      }, { headers: authHeader() });
      toast.success('BAA consent saved to patient file');
      setBaa(b => ({ ...b, hasConsent: true }));
      setShowBaa(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save consent');
    }
  };

  if (loading) return <div className="p-12 text-center text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading room…</div>;
  if (error) {
    return (
      <div className="p-6 max-w-xl mx-auto">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <Card className="p-6 border-rose-200 bg-rose-50">
          <div className="flex items-start gap-3"><AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5" />
            <div><div className="font-medium text-rose-900">Could not start call</div>
              <p className="text-sm text-rose-700 mt-1">{error}</p></div></div>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-screen bg-black flex flex-col" data-testid="telehealth-room">
      <div className="flex items-center justify-between px-4 py-2 bg-[#0A2342] text-white">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" className="text-white" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Leave
          </Button>
          <div className="flex items-center gap-1.5 text-xs">
            <Video className="w-3.5 h-3.5" />
            <span className="font-mono">{config?.room_name}</span>
          </div>
        </div>
        {baa.hasConsent && config?.patient_id && (
          <div className="flex items-center gap-1.5 text-xs text-emerald-300">
            <ShieldCheck className="w-4 h-4" /> BAA signed — v{baa.termsVersion}
          </div>
        )}
      </div>
      <div ref={containerRef} className="flex-1" data-testid="jitsi-container" />

      <BaaConsentDialog
        open={showBaa}
        terms={baa.terms}
        version={baa.termsVersion}
        onConfirm={handleSigned}
        onCancel={() => navigate(-1)}
      />
    </div>
  );
}

function BaaConsentDialog({ open, terms, version, onConfirm, onCancel }) {
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('self');
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error('Please type your full legal name as your signature'); return; }
    if (!agreed) { toast.error('You must agree to continue'); return; }
    setBusy(true);
    try { await onConfirm(name, relationship); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="baa-consent-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A2342]">
            <FileSignature className="w-5 h-5 text-[#0E7490]" /> HIPAA Business Associate Acknowledgement
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-xs text-[#64748B]">Version {version} · Required before joining the visit</p>
          <div className="max-h-44 overflow-y-auto p-3 rounded border border-[#E2E8F0] bg-[#F8FAFC] text-[#0A2342] whitespace-pre-line text-xs leading-relaxed" data-testid="baa-terms-text">
            {terms || 'Loading terms…'}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="sm:col-span-2">
              <Label className="text-xs">Type your full legal name as signature *</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Jane A. Smith" data-testid="baa-signed-name" />
            </div>
            <div>
              <Label className="text-xs">Relationship to patient</Label>
              <select value={relationship} onChange={e => setRelationship(e.target.value)}
                      className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-1.5 bg-white text-sm"
                      data-testid="baa-relationship">
                <option value="self">Self</option>
                <option value="parent">Parent / Guardian</option>
                <option value="spouse">Spouse / Partner</option>
                <option value="caregiver">Caregiver</option>
                <option value="poa">Power of Attorney</option>
                <option value="other">Other authorized representative</option>
              </select>
            </div>
          </div>
          <label className="flex items-start gap-2 text-xs text-[#0A2342]">
            <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                   className="mt-0.5" data-testid="baa-agree-checkbox" />
            <span>I have read, understood, and agree to these terms. I confirm I am authorized to sign this acknowledgement.</span>
          </label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="baa-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={busy || !agreed || !name.trim()}
                  className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white"
                  data-testid="baa-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ShieldCheck className="w-4 h-4 mr-1" />}
            Sign &amp; join visit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
